import React, { useState, useEffect } from 'react';
import { restaurantAPI } from '../../services/api';
import TimeInput12h from '../../components/TimeInput12h';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, day) => {
  acc[day.toLowerCase()] = {
    isOpen: true,
    openTime: '09:00',
    closeTime: '22:00'
  };
  return acc;
}, {});

function OpeningHours() {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    fetchOpeningHours();
  }, []);

  // Convert backend format (open/close/isOff) to frontend format (openTime/closeTime/isOpen)
  const convertFromBackend = (backendHours) => {
    const converted = {};
    DAYS.forEach(day => {
      const dayKey = day.toLowerCase();
      const dayData = backendHours[dayKey];
      if (dayData) {
        converted[dayKey] = {
          isOpen: dayData.isOff !== undefined ? !dayData.isOff : (dayData.isOpen !== undefined ? dayData.isOpen : true),
          openTime: dayData.open || dayData.openTime || '09:00',
          closeTime: dayData.close || dayData.closeTime || '22:00'
        };
      } else {
        converted[dayKey] = { isOpen: true, openTime: '09:00', closeTime: '22:00' };
      }
    });
    return converted;
  };

  // Convert frontend format (openTime/closeTime/isOpen) to backend format (open/close/isOff)
  const convertToBackend = (frontendHours) => {
    const converted = {};
    DAYS.forEach(day => {
      const dayKey = day.toLowerCase();
      const dayData = frontendHours[dayKey];
      if (dayData) {
        converted[dayKey] = {
          isOff: !dayData.isOpen,
          open: dayData.openTime,
          close: dayData.closeTime
        };
      }
    });
    return converted;
  };

  const fetchOpeningHours = async () => {
    try {
      setLoading(true);
      const response = await restaurantAPI.getOpeningHours();
      if (response.data.data?.restaurant?.openingHours) {
        // Convert backend format to frontend format
        setHours(convertFromBackend(response.data.data.restaurant.openingHours));
      } else if (response.data.data?.openingHours) {
        setHours(convertFromBackend(response.data.data.openingHours));
      }
    } catch (error) {
      console.error('Failed to fetch opening hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (day) => {
    setHours(prev => ({
      ...prev,
      [day.toLowerCase()]: {
        ...prev[day.toLowerCase()],
        isOpen: !prev[day.toLowerCase()].isOpen
      }
    }));
    setSaved(false);
  };

  const handleTimeChange = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      [day.toLowerCase()]: {
        ...prev[day.toLowerCase()],
        [field]: value
      }
    }));
    setSaved(false);
  };

  const handleCopyFromPrevious = (dayIndex) => {
    if (dayIndex === 0) return;
    const previousDay = DAYS[dayIndex - 1].toLowerCase();
    const currentDay = DAYS[dayIndex].toLowerCase();

    setHours(prev => ({
      ...prev,
      [currentDay]: { ...prev[previousDay] }
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    // CLIENT-SIDE VALIDATION: every open day must have closeTime > openTime.
    // The backend should also enforce this, but catching it here gives
    // immediate feedback and prevents accidental "closed all day" states
    // caused by swapped times.
    const toMinutes = (t) => {
      if (!t || typeof t !== 'string' || !t.includes(':')) return NaN;
      const [h, m] = t.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
      return h * 60 + m;
    };

    for (const [dayKey, dayHours] of Object.entries(hours)) {
      if (!dayHours?.isOpen) continue;
      const openMin = toMinutes(dayHours.openTime);
      const closeMin = toMinutes(dayHours.closeTime);
      if (Number.isNaN(openMin) || Number.isNaN(closeMin)) {
        setValidationError(`${dayKey}: invalid time format`);
        return;
      }
      // Overnight hours are valid: a restaurant open 08:00 → 01:00 means
      // it closes at 1 AM the next day. Only reject if BOTH times are
      // identical (open === close means zero-length operating window,
      // which is almost certainly an accident).
      if (openMin === closeMin) {
        setValidationError(`${dayKey}: open and close times are the same`);
        return;
      }
    }

    try {
      setSaving(true);
      setValidationError('');
      // Convert to backend format (open/close/isOff) before sending
      const backendHours = convertToBackend(hours);
      await restaurantAPI.updateOpeningHours({ openingHours: backendHours });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save opening hours:', error);
      setValidationError('Failed to save opening hours. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ enabled, onToggle }) => (
    <button
      onClick={onToggle}
      style={{
        width: '52px',
        height: '28px',
        borderRadius: '14px',
        border: 'none',
        background: enabled ? '#FF6961' : '#E2E8F0',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.3s ease',
        boxShadow: enabled ? '0 2px 8px rgba(255, 105, 97, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: '#FFFFFF',
        position: 'absolute',
        top: '2px',
        left: enabled ? '26px' : '2px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
      }} />
    </button>
  );

  if (loading) return (
    <div style={{
      minHeight: '100vh',
      background: '#F8FAFC',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748B',
      fontSize: '16px'
    }}>
      Loading opening hours...
    </div>
  );

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container">
        {/* Header with Flutter-style gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6961, #FF8A84)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 4px 10px rgba(255, 105, 97, 0.15)',
          textAlign: 'center'
        }} className="fade-in">
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#FFFFFF',
            marginBottom: '8px'
          }}>
            🕐 Opening Hours
          </h1>
          <p style={{
            color: '#FFFFFF',
            fontSize: '16px',
            opacity: '0.95'
          }}>
            Set your restaurant's operating hours
          </p>
        </div>

        {/* Success Message */}
        {saved && (
          <div className="fade-in" style={{
            background: '#FFFFFF',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <span style={{ fontSize: '24px' }}>✅</span>
            <span style={{ color: '#10B981', fontWeight: '600', fontSize: '15px' }}>
              Opening hours saved successfully!
            </span>
          </div>
        )}

        {/* Days Schedule - Flutter-style white card */}
        <div className="fade-in" style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {DAYS.map((day, index) => {
              const dayKey = day.toLowerCase();
              const dayHours = hours[dayKey] || { isOpen: true, openTime: '09:00', closeTime: '22:00' };

              return (
                <div key={day} style={{
                  padding: '20px',
                  background: dayHours.isOpen ? '#F8FAFC' : 'rgba(239, 68, 68, 0.04)',
                  borderRadius: '12px',
                  border: dayHours.isOpen ? '1px solid #E2E8F0' : '1px solid rgba(239, 68, 68, 0.15)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    {/* Day Name & Toggle */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      minWidth: '200px'
                    }}>
                      <ToggleSwitch
                        enabled={dayHours.isOpen}
                        onToggle={() => handleToggleDay(day)}
                      />
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: '#0F172A',
                          fontSize: '16px',
                          marginBottom: '2px'
                        }}>
                          {day}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: dayHours.isOpen ? '#10B981' : '#EF4444',
                          fontWeight: '500'
                        }}>
                          {dayHours.isOpen ? 'Open' : 'Closed'}
                        </div>
                      </div>
                    </div>

                    {/* Time Inputs */}
                    {dayHours.isOpen && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{
                            fontSize: '14px',
                            color: '#64748B',
                            fontWeight: '500'
                          }}>
                            Open:
                          </label>
                          <TimeInput12h
                            value={dayHours.openTime}
                            onChange={(val) => handleTimeChange(day, 'openTime', val)}
                          />
                        </div>

                        <span style={{
                          color: '#94A3B8',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>to</span>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{
                            fontSize: '14px',
                            color: '#64748B',
                            fontWeight: '500'
                          }}>
                            Close:
                          </label>
                          <TimeInput12h
                            value={dayHours.closeTime}
                            onChange={(val) => handleTimeChange(day, 'closeTime', val)}
                          />
                        </div>

                        {/* Copy from previous */}
                        {index > 0 && (
                          <button
                            onClick={() => handleCopyFromPrevious(index)}
                            style={{
                              padding: '10px 16px',
                              background: '#FFFFFF',
                              border: '1px solid #E2E8F0',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#64748B',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = '#F8FAFC';
                              e.target.style.borderColor = '#FF6961';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = '#FFFFFF';
                              e.target.style.borderColor = '#E2E8F0';
                            }}
                          >
                            <span>📋</span>
                            <span>Copy from {DAYS[index - 1]}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions - Flutter-style white card */}
        <div className="fade-in" style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#0F172A'
          }}>
            Quick Actions
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const allOpen = {};
                DAYS.forEach(day => {
                  allOpen[day.toLowerCase()] = { isOpen: true, openTime: '09:00', closeTime: '22:00' };
                });
                setHours(allOpen);
                setSaved(false);
              }}
              style={{
                padding: '12px 20px',
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FFFFFF';
                e.target.style.borderColor = '#FF6961';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.08)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F8FAFC';
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span>🌅</span>
              <span>Set All Days Open</span>
            </button>

            <button
              onClick={() => {
                const weekendClosed = { ...hours };
                weekendClosed.saturday = { ...weekendClosed.saturday, isOpen: false };
                weekendClosed.sunday = { ...weekendClosed.sunday, isOpen: false };
                setHours(weekendClosed);
                setSaved(false);
              }}
              style={{
                padding: '12px 20px',
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FFFFFF';
                e.target.style.borderColor = '#FF6961';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.08)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F8FAFC';
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span>🏖️</span>
              <span>Close Weekends</span>
            </button>

            <button
              onClick={() => {
                const extended = {};
                DAYS.forEach(day => {
                  extended[day.toLowerCase()] = { isOpen: true, openTime: '08:00', closeTime: '23:00' };
                });
                setHours(extended);
                setSaved(false);
              }}
              style={{
                padding: '12px 20px',
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FFFFFF';
                e.target.style.borderColor = '#FF6961';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.08)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F8FAFC';
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span>🌙</span>
              <span>Extended Hours (8AM-11PM)</span>
            </button>
          </div>
        </div>

        {/* Validation / save error — inline, no alert() which steals
            focus on Windows Electron and makes inputs unresponsive */}
        {validationError && (
          <div style={{
            textAlign: 'center',
            marginBottom: '16px',
            padding: '12px 20px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            color: '#B91C1C',
            fontWeight: '600',
            fontSize: '14px',
          }}>
            {validationError}
          </div>
        )}

        {/* Save Button - Flutter-style gradient button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '48px' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              minWidth: '240px',
              padding: '16px 32px',
              background: saving ? '#94A3B8' : 'linear-gradient(135deg, #FF6961, #FF8A84)',
              border: 'none',
              borderRadius: '12px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: saving ? 'none' : '0 4px 15px rgba(255, 105, 97, 0.3)',
              transition: 'all 0.3s ease',
              opacity: saving ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 105, 97, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(255, 105, 97, 0.3)';
              }
            }}
          >
            {saving ? (
              <>
                <span className="spinner" style={{ width: '20px', height: '20px' }}></span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span>Save Opening Hours</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OpeningHours;
